/**
 * useVfsContent
 *
 * Fetches the body of a VFS file at an already-rewritten API URL (see
 * `src/lib/vfs-url.ts`). Used by AUI components that take a `*Url` content
 * prop (e.g. `MarkdownEditor.contentUrl`, `AuiTable.rowsUrl`) to load the
 * file body at render time. Re-fetches when:
 *
 *   1. The URL itself changes (typically because the agent baked a fresh
 *      `?v={ms}` cache-bust into a new surface spec).
 *   2. A `vfs.file.updated` event arrives for the file the URL points at.
 *      Lets surface items refresh in place without waiting for the agent
 *      to send a surface update. The refetch uses `cache: 'no-cache'` so
 *      the browser revalidates with origin even though the URL string is
 *      unchanged.
 *
 * On refresh the previously loaded body stays visible until the new body
 * arrives — no flash to an empty state.
 */

import { useEffect, useRef, useState } from 'react';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent, VfsFileEventData } from '../types/events';
import { kbApi } from '../services/api';

export interface VfsContentState {
  data: string | null;
  loading: boolean;
  error: Error | null;
}

const IDLE: VfsContentState = { data: null, loading: false, error: null };

/** Pull (ownerId, path) out of a `/api/v1/vfs/{ownerId}/{path}?...` URL.
 *  Returns null for any URL that isn't a VFS reference (then the hook
 *  just falls back to plain URL-keyed refetch behavior). */
function parseVfsUrl(url: string): { ownerId: string; path: string } | null {
  const match = url.match(/\/api\/v1\/vfs\/([^/?#]+)\/([^?#]+)/);
  if (!match) return null;
  try {
    return {
      ownerId: decodeURIComponent(match[1]),
      path: decodeURIComponent(match[2]),
    };
  } catch {
    return null;
  }
}

/** Pull (kbId, path) out of a `/api/v1/kb/{kbId}/content/{path}?...` URL
 *  (the form `rewriteVfsUrisInString` produces for `kb://` URIs). The KB
 *  endpoint encodes the whole path as one segment, so we decode it back to
 *  the original path. Returns null for non-KB-content URLs. */
function parseKbContentUrl(url: string): { kbId: string; path: string } | null {
  const match = url.match(/\/api\/v1\/kb\/([^/?#]+)\/content\/([^?#]+)/);
  if (!match) return null;
  try {
    return {
      kbId: decodeURIComponent(match[1]),
      path: decodeURIComponent(match[2]),
    };
  } catch {
    return null;
  }
}

export function useVfsContent(url: string | undefined): VfsContentState {
  const [state, setState] = useState<VfsContentState>(() =>
    url ? { data: null, loading: true, error: null } : IDLE,
  );
  // Bumped by the event subscription to force the fetch effect to re-run
  // without changing the URL. Use a counter (not a boolean) so successive
  // events while a fetch is in flight still trigger an additional run.
  const [refetchToken, setRefetchToken] = useState(0);
  const { subscribe } = usePlatformEvents();

  // Subscribe to file-update events for the file this URL points at. Bumps
  // refetchToken on match. Non-VFS/KB URLs (or unparseable ones) skip the
  // subscription entirely and rely on URL-keyed refetch only.
  useEffect(() => {
    if (!url) return;

    // KB content URL: KB file updates arrive as `kb.vfs.file.updated`
    // (kbId embedded in `data`) or `vfs.file.updated` with the KB as the
    // subject. Mirror `useKbCacheReconciler`'s owner/path match.
    const kb = parseKbContentUrl(url);
    if (kb) {
      const { kbId, path } = kb;
      const onEvent = (event: PlatformEvent) => {
        const data = (event.data ?? {}) as Record<string, unknown>;
        const ownerMatch =
          data.kbId === kbId || data.ownerId === kbId || event.subjectId === kbId;
        if (ownerMatch && data.path === path) {
          setRefetchToken((n) => n + 1);
        }
      };
      return subscribe(
        [
          { eventType: 'kb.vfs.file.updated' },
          { eventType: 'vfs.file.updated', subjectId: kbId },
        ],
        onEvent,
      );
    }

    const parsed = parseVfsUrl(url);
    if (!parsed) return;
    const { ownerId, path } = parsed;
    const unsubscribe = subscribe(
      [{ eventType: 'vfs.file.updated', subjectId: ownerId }],
      (event: PlatformEvent) => {
        const data = event.data as unknown as VfsFileEventData;
        if (data?.path === path) {
          setRefetchToken((n) => n + 1);
        }
      },
    );
    return unsubscribe;
  }, [url, subscribe]);

  // Track whether this is a refresh (vs initial load / URL change) so the
  // existing data stays visible during the refetch — no flash.
  const initialFetchRef = useRef(true);
  useEffect(() => {
    if (!url) {
      setState(IDLE);
      initialFetchRef.current = true;
      return;
    }

    let cancelled = false;
    const isRefresh = !initialFetchRef.current;
    setState((prev) =>
      isRefresh && prev.data !== null
        ? { data: prev.data, loading: true, error: null }
        : { data: null, loading: true, error: null },
    );

    // KB content lives behind a JSON-wrapped endpoint. Route it through
    // `kbApi.readText`, which encodes the path and unwraps the envelope to
    // the raw body string (falling back to raw text if a future deploy
    // streams bytes). The endpoint always serves the canonical content, so
    // no cache-buster is needed on refresh.
    const kb = parseKbContentUrl(url);
    const body: Promise<string> = kb
      ? kbApi.readText(kb.kbId, kb.path)
      : fetch(url, {
          credentials: 'include',
          // On refresh, the URL string is the same as before — bypass any
          // browser-side cached response so we actually pick up new bytes.
          cache: isRefresh ? 'no-cache' : 'default',
        }).then(async (res) => {
          if (!res.ok) {
            throw new Error(`Failed to load VFS content (${res.status})`);
          }
          return res.text();
        });

    body
      .then((text) => {
        if (cancelled) return;
        setState({ data: text, loading: false, error: null });
        initialFetchRef.current = false;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ data: null, loading: false, error });
        initialFetchRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [url, refetchToken]);

  // Reset the initial-fetch flag when the URL changes, so a navigation to
  // a different file gets the "blank then load" treatment (initial state),
  // while same-URL refetches via the token stay seamless.
  useEffect(() => {
    initialFetchRef.current = true;
  }, [url]);

  return state;
}
